import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import WhatsAppLink from "./WhatsAppLink";
import AutocompleteInput from "./AutocompleteInput";
import AddressAutocomplete from "./AddressAutocomplete";
import CompagniaField from "./CompagniaField";
import DateInput from "./DateInput";
import Combobox, { DOC_TIPI, DOC_RILASCIATO, COLORI_VEICOLO } from "./Combobox";
import TitoloInput from "./TitoloInput";
import { withCfSesso } from "../lib/cfSesso";
import DecodificaCFButton from "./DecodificaCFButton";
import SignatureField from "./SignatureField";
import { ValidationHint } from "./ValidationHint";
import { validateCodiceFiscale } from "../lib/validators";
import { guardTarga } from "../context/TargaGuard";
import { isValidTarga } from "../lib/targa";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UserRound, Sparkles, ArrowRightLeft, ChevronsUpDown, Loader2, Plus, ChevronDown, KeyRound } from "lucide-react";
import { AreaRiservataPanel, prefetchAreaAccount } from "./AreaRiservataPanel";
import { GdprClientePanel } from "./GdprClientePanel";
import { toast } from "sonner";
import RuoloSinistroField from "./RuoloSinistroField";
import { EsercentiPotestaSection } from "./EsercentiPotestaSection";
import { isMinore } from "../lib/eta";
import ConducenteFormSection from "./ConducenteFormSection";
import LesioniPSCard, { isConducenteFerito } from "./LesioniPSCard";
import IspettoratoField from "./IspettoratoField";
import SegnalatoreField from "./SegnalatoreField";
import MultiCellulare from "./MultiCellulare";
import SoggettoIvaTabs from "./SoggettoIvaTabs";
import { CodiceFiscale } from "codice-fiscale-js";

const EMPTY = {
  soggetto_iva: "NO", cognome: "", nome: "", sesso: "",
  nasc_g: "", nasc_m: "", nasc_a: "",
  comune_nascita: "", comune_nascita_prov: "",
  codice_fiscale: "", professione: "",
  indirizzo: "", numero_civico: "", comune: "", cap: "", provincia: "",
  cell: "", tel_no_cai: false, tel_casa: "", tel_ufficio: "", fax: "", email: "", iban: "", pec: "", codice_destinatario: "",
  firma: "",
  cliente: true,
  segnalatore: "", provvigione_abilitata: false, provvigione_percentuale: "", parcelle: [],
  ruolo: "", assicurazione: "", polizza_n: "",
  scad_g: "", scad_m: "", scad_a: "",
  costruttore: "", modello: "", colore: "", targa: "", data_immatricolazione: "",
  ispettorato: "", veicolo_estero: "NO", visionabile: "", documento: "", numero_doc: "", categoria_patente: "", rilasciato: "",
  luogo_rilascio_doc: "", data_emissione_doc: "", data_scadenza_doc: "",
  ass_sociale: "", reddito_tipo: "", reddito_importo: "",
  cessionario: { carrozzeria_id: "", ragione_sociale: "", titolare: "", codice_fiscale: "", partita_iva: "", via: "", n_civico: "", provincia: "", comune: "", cap: "", iban: "" },
  punti_urto: {},
};

const VISIONABILE = ["Propria abitazione", "Carrozzeria", "Depositeria", "Altro"];
const PATENTE_CATEGORIE = ["AM", "A1", "A2", "A", "B1", "B", "BE", "C1", "C1E", "C", "CE", "D1", "D1E", "D", "DE"];
const ASS_SOCIALE = ["Nessuna", "INPS", "INAIL", "Altra"];
const REDDITO_TIPI = ["Nessuno", "Dipendente", "Autonomo/Libero professionista", "Pensionato", "Altro"];
const PUNTI_URTO = [
  ["frontale", "Frontale"], ["ant_sx", "Anteriore sx"], ["ant_dx", "Anteriore dx"],
  ["posteriore", "Posteriore"], ["lat_sx", "Laterale sx"], ["lat_dx", "Laterale dx"],
  ["distr_totale", "Distr. totale"], ["post_sx", "Posteriore sx"], ["post_dx", "Posteriore dx"],
];
const LBL = "text-slate-300 text-xs uppercase tracking-wide";
const INPUT_CLS = "mt-1.5 h-9 bg-slate-900/60 border-slate-700";

const pad2 = (n) => String(n).padStart(2, "0");
const tripleToISO = (g, m, a) => (g && m && a ? `${a}-${pad2(m)}-${pad2(g)}` : "");

// Calcola la data di scadenza documento in base alle durate legali italiane.
function computeScadenza(documento, emissione, birth) {
  if (!documento || !emissione) return "";
  const d = new Date(emissione);
  if (isNaN(d.getTime())) return "";
  const doc = documento.toLowerCase();
  let ageAt = null;
  if (birth) {
    const b = new Date(birth);
    if (!isNaN(b.getTime())) {
      ageAt = d.getFullYear() - b.getFullYear();
      const mm = d.getMonth() - b.getMonth();
      if (mm < 0 || (mm === 0 && d.getDate() < b.getDate())) ageAt--;
    }
  }
  let years = null;
  if (doc.includes("identit")) years = ageAt == null ? 10 : ageAt < 3 ? 3 : ageAt < 18 ? 5 : 10;
  else if (doc.includes("passaporto")) years = 10;
  else if (doc.includes("patente")) years = ageAt == null ? 10 : ageAt < 50 ? 10 : ageAt < 70 ? 5 : ageAt < 80 ? 3 : 2;
  else if (doc.includes("permesso di soggiorno")) years = 2;
  if (years == null) return "";
  const e = new Date(d);
  e.setFullYear(e.getFullYear() + years);
  return `${e.getFullYear()}-${pad2(e.getMonth() + 1)}-${pad2(e.getDate())}`;
}

function parseAnyDate(s) {
  if (!s) return null;
  const str = String(s).trim();
  let y, m, d;
  if (str.includes("/")) { const [a, b, c] = str.split("/"); d = +a; m = +b; y = +c; }
  else if (str.includes("-")) { const [a, b, c] = str.split("-"); y = +a; m = +b; d = +c; }
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? null : dt;
}

// Componenti campo definiti a livello di modulo: identità stabile tra i render -> nessuna perdita di focus durante la digitazione.
function FieldText({ label, value, onChange, testId, mono, transform, wa, extra, onExtra, principal, onPrincipal, labels, onLabels }) {
  const input = <Input value={value || ""} onChange={(e) => onChange(transform ? transform(e.target.value) : e.target.value)}
    data-testid={testId} className={`${INPUT_CLS} ${mono ? "font-mono" : ""} ${onExtra ? "!mt-0" : ""}`} />;
  return (
    <div>
      <Label className={`${LBL} ${wa ? "inline-flex items-center gap-1.5" : ""}`}>{label}{wa ? <WhatsAppLink number={value} size="h-3.5 w-3.5" testId={testId ? `${testId}-wa` : undefined} /> : null}</Label>
      {onExtra ? <div className="mt-1.5"><MultiCellulare extra={extra} onExtra={onExtra} principal={principal} onPrincipal={onPrincipal} labels={labels} onLabels={onLabels} testId={testId}>{input}</MultiCellulare></div> : input}
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, placeholder, testId }) {
  return (
    <div>
      <Label className={LBL}>{label}</Label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className={`${INPUT_CLS} text-slate-100`}><SelectValue placeholder={placeholder || "Seleziona"} /></SelectTrigger>
        <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function DateField({ label, value, onChange, testId }) {  return (
    <div>
      <Label className={LBL}>{label}</Label>
      <div className="mt-1.5">
        <DateInput value={value} onChange={onChange} testId={testId} className="h-9 bg-slate-900/60 border-slate-700" />
      </div>
    </div>
  );
}

function FieldAuto({ label, value, onChange, source, extraParams, onItem, testId }) {
  return (
    <div>
      <Label className={LBL}>{label}</Label>
      <div className="mt-1.5">
        <AutocompleteInput value={value} onChange={onChange} source={source} extraParams={extraParams} onSelectItem={onItem} testId={testId} />
      </div>
    </div>
  );
}

const NEW_CARR_EMPTY = { ragione_sociale: "", titolare: "", codice_fiscale: "", partita_iva: "", via: "", n_civico: "", comune: "", provincia: "", cap: "" };

function NuovaCarrozzeriaModal({ open, onClose, onCreated }) {
  const [nf, setNf] = useState(NEW_CARR_EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setNf(NEW_CARR_EMPTY); }, [open]);
  const setN = (k, v) => setNf((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!nf.ragione_sociale.trim()) { toast.error("Inserisci la Ragione Sociale"); return; }
    setSaving(true);
    try {
      const payload = {
        denominazione: nf.ragione_sociale, referente: nf.titolare, codice_fiscale: nf.codice_fiscale,
        partita_iva: nf.partita_iva, indirizzo: nf.via, numero_civico: nf.n_civico,
        comune: nf.comune, provincia: nf.provincia, cap: nf.cap,
        solo_iniziali_copertina: !!nf.solo_iniziali_copertina,
      };
      const { data } = await api.post("/anagrafiche/carrozzieri", payload);
      toast.success("Carrozzeria salvata in anagrafica");
      onCreated(data);
    } catch {
      toast.error("Errore nel salvataggio della carrozzeria");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="nuova-carrozzeria-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-cyan-500" /> Nuova Carrozzeria</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-4 py-1">
          <div className="md:col-span-2"><FieldText label="Ragione Sociale" value={nf.ragione_sociale} onChange={(v) => setN("ragione_sociale", v)} testId="nc-ragione_sociale" /></div>
          <FieldText label="Titolare" value={nf.titolare} onChange={(v) => setN("titolare", v)} testId="nc-titolare" />
          <FieldText label="Codice Fiscale" value={nf.codice_fiscale} onChange={(v) => setN("codice_fiscale", v.toUpperCase())} testId="nc-codice_fiscale" mono />
          <FieldText label="P.IVA" value={nf.partita_iva} onChange={(v) => setN("partita_iva", v)} testId="nc-partita_iva" mono />
          <FieldText label="Via" value={nf.via} onChange={(v) => setN("via", v)} testId="nc-via" />
          <FieldText label="N. Civico" value={nf.n_civico} onChange={(v) => setN("n_civico", v)} testId="nc-n_civico" />
          <FieldText label="Comune" value={nf.comune} onChange={(v) => setN("comune", v)} testId="nc-comune" />
          <FieldText label="Provincia" value={nf.provincia} onChange={(v) => setN("provincia", v)} testId="nc-provincia" />
          <FieldText label="CAP" value={nf.cap} onChange={(v) => setN("cap", v)} testId="nc-cap" />
          <div className="md:col-span-2 flex items-center gap-2">
            <Checkbox checked={!!nf.solo_iniziali_copertina} onCheckedChange={(v) => setN("solo_iniziali_copertina", !!v)} data-testid="nc-solo-iniziali-copertina" />
            <Label className="text-slate-300 text-sm cursor-pointer" onClick={() => setN("solo_iniziali_copertina", !nf.solo_iniziali_copertina)}>Mostra solo le iniziali nella copertina</Label>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="nc-cancel">Annulla</Button>
          <Button type="button" onClick={save} disabled={saving} data-testid="nc-save" className="bg-cyan-600 hover:bg-cyan-500 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CessionarioSelect({ value, onPick, testId }) {
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    setLoading(true);
    api.get("/anagrafiche/carrozzieri").then(({ data }) => setOpts(Array.isArray(data) ? data : [])).catch(() => setOpts([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const term = q.trim().toLowerCase();
  const filtered = term ? opts.filter((o) => `${o.denominazione || ""} ${o.partita_iva || ""} ${o.comune || ""}`.toLowerCase().includes(term)) : opts;
  const handleCreated = (rec) => {
    setOpts((prev) => [rec, ...prev]);
    onPick(rec);
    setNewOpen(false);
    setOpen(false);
    setQ("");
  };
  return (
    <div className="relative w-full" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testId}
        className="h-9 w-full flex items-center justify-between rounded-md bg-slate-900/60 border border-slate-700 px-3 text-sm text-slate-100 hover:border-slate-600">
        <span className={value ? "text-slate-100" : "text-slate-500"}>{value || "Seleziona carrozzeria"}</span>
        <ChevronsUpDown className="h-4 w-4 text-slate-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-700 bg-[#121824] shadow-xl" data-testid={`${testId}-list`}>
          <div className="p-2 border-b border-slate-800">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca carrozzeria…" data-testid={`${testId}-search`}
              className="w-full h-8 px-2 rounded-md bg-slate-900/60 border border-slate-700 text-sm text-slate-100 focus:outline-none focus:border-cyan-500/50" />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-4 text-center"><Loader2 className="h-4 w-4 animate-spin text-amber-500 inline" /></div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-slate-500">Nessuna carrozzeria in anagrafica</div>
            ) : filtered.map((o) => (
              <button key={o.id} type="button" onClick={() => { onPick(o); setOpen(false); setQ(""); }} data-testid={`${testId}-opt`}
                className="w-full text-left px-3 py-2 hover:bg-slate-800/60 border-b border-slate-800 last:border-0">
                <div className="text-sm font-medium text-slate-200">{o.denominazione || "—"}</div>
                <div className="text-[11px] text-slate-400">{[o.partita_iva, o.comune].filter(Boolean).join(" · ") || "—"}</div>
              </button>
            ))}
          </div>
          <button type="button" onClick={() => { setNewOpen(true); setOpen(false); }} data-testid={`${testId}-nuova`}
            className="w-full flex items-center gap-2 px-3 py-2.5 border-t border-slate-700 text-sm font-medium text-cyan-400 hover:bg-slate-800/60">
            <Plus className="h-4 w-4" /> Nuova carrozzeria
          </button>
        </div>
      )}
      {newOpen && <NuovaCarrozzeriaModal open={newOpen} onClose={() => setNewOpen(false)} onCreated={handleCreated} />}
    </div>
  );
}

export default function AssistitoDialog({ open, onOpenChange, initial, onSave, dataSinistro, praticaId, conducenteEnabled = false, conducenteValue }) {
  const [form, setForm] = useState(EMPTY);
  const [cond, setCond] = useState({});
  const [saving, setSaving] = useState(false);
  const [cfTouched, setCfTouched] = useState(false);
  const [cfBlur, setCfBlur] = useState(false);
  const [scadTouched, setScadTouched] = useState(false);
  const [docTipiCustom, setDocTipiCustom] = useState([]);
  const [docRilasciatoCustom, setDocRilasciatoCustom] = useState([]);
  const [areaOpen, setAreaOpen] = useState(false);
  useEffect(() => { if (open) setAreaOpen(false); }, [open]);
  useEffect(() => { if (open && form?.cliente_id) prefetchAreaAccount(form.cliente_id).catch(() => {}); }, [open, form?.cliente_id]);

  useEffect(() => {
    if (open) {
      const init = { ...EMPTY, ...(initial || {}), punti_urto: { ...(initial?.punti_urto || {}) }, cessionario: { ...EMPTY.cessionario, ...(initial?.cessionario || {}) } };
      // Normalizza gli alias provenienti dal wizard/anagrafica verso i campi della scheda
      // (evita campi vuoti: sesso/nascita, veicolo, polizza, scadenza immatricolazione).
      const _v0 = (Array.isArray(init.veicoli) && init.veicoli[0]) ? init.veicoli[0] : {};
      if (!init.cell && init.cellulare) init.cell = init.cellulare;
      if (!init.comune_nascita && init.luogo_nascita) init.comune_nascita = init.luogo_nascita;
      if (!init.comune_nascita_prov && init.provincia_nascita) init.comune_nascita_prov = init.provincia_nascita;
      if (!init.costruttore) init.costruttore = _v0.marca || "";
      if (!init.modello) init.modello = _v0.modello || "";
      if (!init.colore) init.colore = _v0.colore || "";
      if (!init.targa) init.targa = _v0.targa || "";
      if (!init.polizza_n) init.polizza_n = init.numero_polizza || _v0.numero_polizza || "";
      if (!init.data_immatricolazione && /^\d{4}-\d{2}-\d{2}/.test(String(_v0.anno_immatricolazione || ""))) init.data_immatricolazione = _v0.anno_immatricolazione;
      if (!init.assicurazione) init.assicurazione = init.compagnia || _v0.compagnia || "";
      if (!init.nasc_g && !init.nasc_m && !init.nasc_a && init.data_nascita) {
        const [a, m, g] = String(init.data_nascita).split("-");
        init.nasc_a = a || ""; init.nasc_m = m || ""; init.nasc_g = g || "";
      }
      const _scad = init.scadenza_polizza || _v0.scadenza_polizza || "";
      if (!init.scad_g && !init.scad_m && !init.scad_a && _scad) {
        const [a, m, g] = String(_scad).split("-");
        init.scad_a = a || ""; init.scad_m = m || ""; init.scad_g = g || "";
      }
      if (/^\s*nessun[aoe]?\s*$/i.test(init.assicurazione || "")) init.assicurazione = "";
      setForm(init);
      setCond(conducenteValue && typeof conducenteValue === "object" ? conducenteValue : {});
      setSaving(false);
      setCfTouched(!!(init.codice_fiscale || "").trim());
      setScadTouched(!!init.data_scadenza_doc);
      api.get("/studio/liste-doc").then(({ data }) => { setDocTipiCustom(data?.doc_tipi || []); setDocRilasciatoCustom(data?.doc_rilasciato || []); }).catch(() => {});
    }
  }, [open, initial, conducenteValue]);

  const saveListaDoc = async (campo, valore, predefiniti, custom, setCustom) => {
    const v = (valore || "").trim();
    if (!v) return;
    if ([...predefiniti, ...custom].some((x) => x.toLowerCase() === v.toLowerCase())) return;
    setCustom((c) => [...c, v]);
    try { await api.post("/studio/liste-doc", { campo, valore: v }); } catch { /* best effort */ }
  };

  const set = (k, v) => setForm((f) => withCfSesso(f, k, v));
  const setCess = (k, v) => setForm((f) => ({ ...f, cessionario: { ...f.cessionario, [k]: v } }));
  const pickCess = (rec) => setForm((f) => ({
    ...f,
    cessionario: {
      ...f.cessionario,
      carrozzeria_id: rec.id,
      ragione_sociale: rec.denominazione || "",
      titolare: rec.referente || "",
      codice_fiscale: rec.codice_fiscale || "",
      partita_iva: rec.partita_iva || "",
      via: rec.indirizzo || "",
      n_civico: rec.numero_civico || "",
      provincia: rec.provincia || "",
      comune: rec.comune || "",
      cap: rec.cap || "",
      iban: rec.iban || "",
    },
  }));

  const setTriple = (prefix, iso) => {
    if (!iso) { setForm((f) => ({ ...f, [`${prefix}_g`]: "", [`${prefix}_m`]: "", [`${prefix}_a`]: "" })); return; }
    const [a, m, g] = iso.split("-");
    setForm((f) => ({ ...f, [`${prefix}_g`]: g, [`${prefix}_m`]: m, [`${prefix}_a`]: a }));
  };

  const eta = useMemo(() => {
    const nasc = parseAnyDate(`${form.nasc_g}/${form.nasc_m}/${form.nasc_a}`);
    const sx = parseAnyDate(dataSinistro);
    if (!nasc || !sx) return "";
    let a = sx.getFullYear() - nasc.getFullYear();
    const mm = sx.getMonth() - nasc.getMonth();
    if (mm < 0 || (mm === 0 && sx.getDate() < nasc.getDate())) a--;
    return a >= 0 && a < 130 ? String(a) : "";
  }, [form.nasc_g, form.nasc_m, form.nasc_a, dataSinistro]);

  const computeCF = () => {
    try {
      return new CodiceFiscale({
        name: form.nome, surname: form.cognome,
        gender: form.sesso === "Femmina" ? "F" : "M",
        day: +form.nasc_g, month: +form.nasc_m, year: +form.nasc_a,
        birthplace: form.comune_nascita, birthplaceProvincia: form.comune_nascita_prov || undefined,
      }).toString();
    } catch (e) { return null; }
  };
  // Ricalcolo automatico del CF a ogni modifica dei dati anagrafici, finché non si modifica il CF a mano
  useEffect(() => {
    if (cfTouched || form.soggetto_iva === "SI") return;
    if (!form.nome || !form.cognome || !form.sesso || !form.comune_nascita || !form.nasc_g || !form.nasc_m || !form.nasc_a) return;
    const cf = computeCF();
    if (cf) setForm((f) => (f.codice_fiscale === cf ? f : { ...f, codice_fiscale: cf }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.nome, form.cognome, form.sesso, form.comune_nascita, form.comune_nascita_prov, form.nasc_g, form.nasc_m, form.nasc_a, form.soggetto_iva, cfTouched]);
  const generaCF = () => {
    const cf = computeCF();
    if (cf) { set("codice_fiscale", cf); setCfTouched(false); toast.success("Codice fiscale generato"); }
    else toast.error("Impossibile generare il CF: compila cognome, nome, sesso, data e comune di nascita corretti");
  };

  // Calcolo automatico della data di scadenza documento (durate legali IT), finché non modificata a mano.
  useEffect(() => {
    if (scadTouched) return;
    const birth = tripleToISO(form.nasc_g, form.nasc_m, form.nasc_a);
    const sc = computeScadenza(form.documento, form.data_emissione_doc, birth);
    if (sc && sc !== form.data_scadenza_doc) setForm((f) => ({ ...f, data_scadenza_doc: sc }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.documento, form.data_emissione_doc, form.nasc_g, form.nasc_m, form.nasc_a, scadTouched]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cognome.trim() && !form.nome.trim()) { toast.error("Inserisci almeno cognome o nome"); return; }
    setSaving(true);
    try {
      const { action, match } = await guardTarga(form.targa, form.cliente_id || initial?.cliente_id, praticaId);
      const condPayload = conducenteEnabled ? { conducente: cond } : {};
      if (action === "existing" && match?.record) { const mapped = mapAnagToForm(match.record); await onSave({ ...mapped, compagnia: mapped.assicurazione || "", ...condPayload }); }
      else { await onSave({ ...form, compagnia: form.assicurazione || "", ...condPayload }); }
    }
    finally { setSaving(false); }
  };

  const mapAnagToForm = (rec) => {
    const [a, m, g] = String(rec.data_nascita || "").split("-");
    return {
      ...form,
      cliente_id: rec.id,
      cognome: rec.cognome || form.cognome,
      nome: rec.nome || form.nome,
      codice_fiscale: rec.codice_fiscale || form.codice_fiscale,
      professione: rec.professione || form.professione,
      indirizzo: rec.indirizzo || form.indirizzo,
      numero_civico: rec.numero_civico || form.numero_civico,
      comune: rec.comune || form.comune,
      cap: rec.cap || form.cap,
      provincia: rec.provincia || form.provincia,
      cell: rec.cellulare || form.cell,
      cellulari_extra: rec.cellulari_extra || form.cellulari_extra,
      cell_principale: rec.cellulari_extra ? (rec.cell_principale || 0) : form.cell_principale,
      cellulari_etichette: rec.cellulari_etichette || form.cellulari_etichette,
      tel_casa: rec.telefono || form.tel_casa,
      email: rec.email || form.email,
      pec: rec.pec || form.pec,
      iban: rec.iban || form.iban,
      codice_destinatario: rec.codice_destinatario || form.codice_destinatario,
      nasc_g: g || form.nasc_g,
      nasc_m: m || form.nasc_m,
      nasc_a: a || form.nasc_a,
      soggetto_iva: ["SI", "E"].includes(String(rec.soggetto_iva || "").toUpperCase()) ? "SI" : (rec.soggetto_iva || form.soggetto_iva),
      partita_iva: rec.partita_iva || form.partita_iva,
      tel_ufficio: rec.tel_ufficio || form.tel_ufficio,
      ...Object.fromEntries(Object.entries(rec).filter(([k, v]) => k.startsWith("rappr_") && v)),
    };
  };

  const targaInvalid = form.targa && !isValidTarga(form.targa);
  const isIva = form.soggetto_iva === "SI";
  const isCedente = (form.ruolo || "").trim().toLowerCase() === "cedente";
  const noVehicle = ["pedone", "ciclista", "trasportato"].some((k) => (form.ruolo || "").toLowerCase().includes(k));
  const showVehicle = !noVehicle;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="assistito-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2 tracking-wide">
            <UserRound className="h-5 w-5 text-amber-400" /> SCHEDA ANAGRAFICA
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} data-testid="assistito-form">
          <div className="mb-4 rounded-lg border border-amber-300" data-testid="scheda-area-riservata">
            <button type="button" onClick={() => setAreaOpen((o) => !o)} data-testid="scheda-area-riservata-toggle" aria-expanded={areaOpen}
              className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-amber-800 hover:bg-amber-50 rounded-lg transition-colors">
              <span className="flex items-center gap-2"><KeyRound className="h-4 w-4" />Area Riservata Cliente</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${areaOpen ? "rotate-180" : ""}`} />
            </button>
            {areaOpen && (
              <div className="px-3 pb-3" data-testid="scheda-area-riservata-body">
                {form.cliente_id
                  ? <AreaRiservataPanel anagId={form.cliente_id} defaultEmail={form.email} />
                  : <p className="text-xs text-slate-500 px-1" data-testid="scheda-area-riservata-nolink">Salva prima la scheda: l'account si crea dopo il collegamento del cliente all'anagrafica.</p>}
              </div>
            )}
          </div>
          {form.cliente_id && <div className="mb-4"><GdprClientePanel anagId={form.cliente_id} /></div>}
          <div className="mb-5 rounded-lg border border-sky-500/30 bg-sky-500/[0.05] p-4" data-testid="scheda-cliente-flag">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={!!form.cliente} onCheckedChange={(v) => set("cliente", !!v)} data-testid="a-cliente-flag" />
              <span className="text-sm text-slate-100 font-medium">Cliente dello studio</span>
            </label>
            <p className="mt-1 text-[11px] text-slate-500">Se attivo, questo soggetto è un cliente dello studio e verrà mostrato con il badge <b className="text-sky-300">CLIENTE</b> accanto al nome.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
            {/* COLONNA SINISTRA */}
            <div className="space-y-4">
              <div>
                <Label className={LBL}>Soggetto IVA</Label>
                <div className="mt-1.5 flex gap-2" data-testid="a-soggetto_iva">
                  {["SI", "NO"].map((o) => (
                    <button type="button" key={o} onClick={() => set("soggetto_iva", o)} data-testid={`a-soggetto_iva-${o}`}
                      className={`h-9 px-4 rounded-md border text-sm font-medium transition-colors ${form.soggetto_iva === o ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>{o}</button>
                  ))}
                </div>
              </div>
              {isIva ? (<>
                <SoggettoIvaTabs form={form} set={set} setForm={setForm} testId="a-iva" />
                <FieldText label="IBAN" value={form.iban} onChange={(v) => set("iban", v)} testId="a-iban" mono />
              </>) : (<>
              <div><Label className={LBL}>Titolo</Label><TitoloInput value={form.titolo} onChange={(v) => set("titolo", v)} testId="a-titolo" className="mt-1" /></div>
              <FieldText label="Cognome" value={form.cognome} onChange={(v) => set("cognome", v)} testId="a-cognome" />
              <FieldText label="Nome" value={form.nome} onChange={(v) => set("nome", v)} testId="a-nome" />
              {!isIva && (
                <>
                  <FieldSelect label="Sesso" value={form.sesso} onChange={(v) => set("sesso", v)} options={["Maschio", "Femmina"]} testId="a-sesso" />
                  <DateField label="Data di nascita" value={tripleToISO(form.nasc_g, form.nasc_m, form.nasc_a)}
                    onChange={(iso) => setTriple("nasc", iso)} testId="a-nasc" />
                  <div>
                    <Label className={LBL}>Età in data Sx</Label>
                    <Input value={eta} readOnly disabled data-testid="a-eta" className={`${INPUT_CLS} text-slate-400`} />
                  </div>
                  <FieldAuto label="Comune di nascita" value={form.comune_nascita} onChange={(v) => set("comune_nascita", v)} source="comuni" onItem={(it) => set("comune_nascita_prov", it.provincia || "")} testId="a-comune_nascita" />
                </>
              )}
              <div>
                <Label className={LBL}>{isIva ? "P.IVA" : "Cod. Fisc. / P.IVA"}</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <Input value={form.codice_fiscale || ""} onChange={(e) => { setCfTouched(true); set("codice_fiscale", e.target.value.toUpperCase()); }} onBlur={() => setCfBlur(true)} data-testid="a-codice_fiscale" className="h-9 bg-slate-900/60 border-slate-700 font-mono flex-1" />
                  {!isIva && (
                    <Button type="button" onClick={generaCF} data-testid="a-genera-cf" title="Genera codice fiscale" className="h-9 bg-cyan-600 hover:bg-cyan-700 text-white shrink-0"><Sparkles className="h-4 w-4 mr-1" /> Genera CF</Button>
                  )}
                  {!isIva && (
                    <DecodificaCFButton cf={form.codice_fiscale} testId="a-decodifica-cf" className="h-9 shrink-0"
                      onDecode={(d) => setForm((prev) => ({ ...prev, sesso: d.sesso === "F" ? "Femmina" : "Maschio", nasc_g: d.nasc_g, nasc_m: d.nasc_m, nasc_a: d.nasc_a, comune_nascita: d.comune_nascita, comune_nascita_prov: d.provincia_nascita, cognome: prev.cognome || d.cognomeCode, nome: prev.nome || d.nomeCode }))} />
                  )}
                </div>
                {!isIva && <ValidationHint result={validateCodiceFiscale(form.codice_fiscale)} show={cfBlur} testId="a-cf" />}
              </div>
              <FieldText label="Professione" value={form.professione} onChange={(v) => set("professione", v)} testId="a-professione" />
              <div>
                <Label className={LBL}>Indirizzo</Label>
                <div className="mt-1.5"><AddressAutocomplete value={form.indirizzo} onChange={(v) => set("indirizzo", v)} onPick={(p) => setForm((f) => ({ ...f, indirizzo: p.indirizzo, comune: p.comune, cap: p.cap, provincia: p.provincia }))} city={form.comune} prov={form.provincia} testId="a-indirizzo" /></div>
              </div>
              <FieldText label="N° Civ." value={form.numero_civico} onChange={(v) => set("numero_civico", v)} testId="a-numero_civico" />
              <FieldAuto label="Comune" value={form.comune} onChange={(v) => set("comune", v)} source="comuni" onItem={(it) => { set("provincia", it.provincia || ""); set("cap", it.cap || ""); }} testId="a-comune" />
              <FieldText label="CAP" value={form.cap} onChange={(v) => set("cap", v)} testId="a-cap" />
              <FieldAuto label="Provincia" value={form.provincia} onChange={(v) => set("provincia", v)} source="province" testId="a-provincia" />
              <div>
                <FieldText label="Cell" value={form.cell} onChange={(v) => set("cell", v)} testId="a-cell" wa extra={form.cellulari_extra} onExtra={(v) => set("cellulari_extra", v)} principal={form.cell_principale} onPrincipal={(v) => set("cell_principale", v)} labels={form.cellulari_etichette} onLabels={(v) => set("cellulari_etichette", v)} />
                <label className="mt-1 flex items-center gap-1.5 cursor-pointer" data-testid="a-tel-no-cai-wrap">
                  <Checkbox checked={!!form.tel_no_cai} onCheckedChange={(v) => set("tel_no_cai", !!v)} data-testid="a-tel-no-cai" className="h-3.5 w-3.5" />
                  <span className="text-[10px] text-slate-400">Non riportare nella CAI</span>
                </label>
              </div>
              <FieldText label="Tel. Casa" value={form.tel_casa} onChange={(v) => set("tel_casa", v)} testId="a-tel_casa" />
              <FieldText label="Tel. Ufficio" value={form.tel_ufficio} onChange={(v) => set("tel_ufficio", v)} testId="a-tel_ufficio" />
              <FieldText label="Fax" value={form.fax} onChange={(v) => set("fax", v)} testId="a-fax" />
              <FieldText label="E-mail" value={form.email} onChange={(v) => set("email", v)} testId="a-email" />
              <FieldText label="IBAN" value={form.iban} onChange={(v) => set("iban", v)} testId="a-iban" mono />
              <FieldText label="PEC" value={form.pec} onChange={(v) => set("pec", v)} testId="a-pec" />
              <FieldText label="Codice Destinatario" value={form.codice_destinatario} onChange={(v) => set("codice_destinatario", v)} testId="a-codice_destinatario" mono />
              </>)}
              <div>
                <Label className={LBL}>Firma cliente</Label>
                <SignatureField value={form.firma} onChange={(v) => set("firma", v)} testId="a-firma" />
              </div>
            </div>

            {/* COLONNA DESTRA */}
            <div className="space-y-4">
              <RuoloSinistroField label="Ruolo Sinistro" value={form.ruolo} onChange={(v) => set("ruolo", v)} testId="a-ruolo" />
              {isMinore(form) && <EsercentiPotestaSection value={form.esercenti_potesta} indirizzoMinore={{ via: form.indirizzo, civico: form.numero_civico, comune: form.comune, provincia: form.provincia, cap: form.cap }} onChange={(v) => setForm((f) => ({ ...f, esercenti_potesta: v }))} />}
              {conducenteEnabled && <ConducenteFormSection value={cond} onChange={setCond} role={form.ruolo} subjectWord="il cliente" testId="a-cond" />}
              {isConducenteFerito(form.ruolo) && <LesioniPSCard form={form} set={set} testId="a-lesioni-ps" />}
              {showVehicle && (
                <>
                  <div>
                    <Label className={LBL}>Assicurazione</Label>
                    <div className="mt-1.5">
                      <CompagniaField value={form.assicurazione} onChange={(v) => set("assicurazione", v)} testId="a-assicurazione" />
                    </div>
                  </div>
                  <IspettoratoField label="Ispettorato" value={form.ispettorato} onChange={(v) => set("ispettorato", v)} compagnia={form.assicurazione} testId="a-ispettorato" />
                  <FieldText label="Polizza N" value={form.polizza_n} onChange={(v) => set("polizza_n", v)} testId="a-polizza_n" />
                  <DateField label="Scadenza" value={tripleToISO(form.scad_g, form.scad_m, form.scad_a)}
                    onChange={(iso) => setTriple("scad", iso)} testId="a-scad" />
                  <FieldAuto label="Costruttore" value={form.costruttore} onChange={(v) => set("costruttore", v)} source="marche" onItem={() => set("modello", "")} testId="a-costruttore" />
                  <FieldAuto label="Modello" value={form.modello} onChange={(v) => set("modello", v)} source="modelli" extraParams={{ marca: form.costruttore || "" }} testId="a-modello" />
                  <div>
                    <Label className={LBL}>Colore veicolo</Label>
                    <div className="mt-1.5"><Combobox value={form.colore} onChange={(v) => set("colore", v)} options={COLORI_VEICOLO} placeholder="Scegli o scrivi un colore" testId="a-colore" /></div>
                  </div>
                  <div>
                    <Label className={LBL}>Targa</Label>
                    <Input value={form.targa || ""} onChange={(e) => set("targa", e.target.value.toUpperCase())} data-testid="a-targa" className={`${INPUT_CLS} font-mono ${targaInvalid ? "border-red-500/60" : ""}`} />
                    {targaInvalid && <p className="mt-1 text-[11px] text-red-400">Formato targa non valido (es. AB123CD)</p>}
                  </div>
                  <DateField label="Data immatricolazione" value={form.data_immatricolazione} onChange={(v) => set("data_immatricolazione", v)} testId="a-data_immatricolazione" />
                  <FieldSelect label="Veicolo estero" value={form.veicolo_estero} onChange={(v) => set("veicolo_estero", v)} options={["SI", "NO"]} testId="a-veicolo_estero" />
                  <FieldSelect label="Visionabile" value={form.visionabile} onChange={(v) => set("visionabile", v)} options={VISIONABILE} testId="a-visionabile" />
                </>
              )}
              <div>
                <Label className={LBL}>Tipo documento</Label>
                <div className="mt-1.5"><Combobox value={form.documento} onChange={(v) => set("documento", v)} options={[...DOC_TIPI, ...docTipiCustom]} noUppercase onBlur={(e) => saveListaDoc("doc_tipi", e.target.value, DOC_TIPI, docTipiCustom, setDocTipiCustom)} testId="a-documento" /></div>
              </div>
              <FieldText label="Numero documento" value={form.numero_doc} onChange={(v) => set("numero_doc", v)} testId="a-numero_doc" />
              {String(form.documento || "").toLowerCase().includes("patente") && (
                <div>
                  <Label className={LBL}>Categoria patente</Label>
                  <div className="mt-1.5"><Combobox value={form.categoria_patente} onChange={(v) => set("categoria_patente", v)} options={PATENTE_CATEGORIE} placeholder="A, B, C, BE, ..." testId="a-categoria_patente" /></div>
                </div>
              )}
              <div>
                <Label className={LBL}>Rilasciato da</Label>
                <div className="mt-1.5"><Combobox value={form.rilasciato} onChange={(v) => set("rilasciato", v)} options={[...DOC_RILASCIATO, ...docRilasciatoCustom]} noUppercase onBlur={(e) => saveListaDoc("doc_rilasciato", e.target.value, DOC_RILASCIATO, docRilasciatoCustom, setDocRilasciatoCustom)} testId="a-rilasciato" /></div>
              </div>
              <FieldAuto label="Luogo di rilascio" value={form.luogo_rilascio_doc} onChange={(v) => set("luogo_rilascio_doc", v)} source="comuni" testId="a-luogo_rilascio_doc" />
              <DateField label="Data rilascio" value={form.data_emissione_doc} onChange={(v) => set("data_emissione_doc", v)} testId="a-data_emissione_doc" />
              <DateField label="Data scadenza" value={form.data_scadenza_doc} onChange={(v) => { setScadTouched(true); set("data_scadenza_doc", v); }} testId="a-data_scadenza_doc" />
              <FieldSelect label="Ass. Sociale" value={form.ass_sociale} onChange={(v) => set("ass_sociale", v)} options={ASS_SOCIALE} testId="a-ass_sociale" />
              <div className="grid grid-cols-2 gap-3">
                <FieldSelect label="Reddito" value={form.reddito_tipo} onChange={(v) => set("reddito_tipo", v)} options={REDDITO_TIPI} testId="a-reddito_tipo" />
                <FieldText label="Importo (€)" value={form.reddito_importo} onChange={(v) => set("reddito_importo", v)} testId="a-reddito_importo" />
              </div>
              <div>
                <Label className={LBL}>Punti d'urto / Parti danneggiate</Label>
                <div className="mt-1.5 grid grid-cols-3 gap-2" data-testid="a-punti_urto">
                  {PUNTI_URTO.map(([k, label]) => (
                    <button type="button" key={k} onClick={() => setForm((f) => ({ ...f, punti_urto: { ...f.punti_urto, [k]: !f.punti_urto[k] } }))}
                      data-testid={`a-urto-${k}`}
                      className={`h-9 px-2 rounded-md border text-xs font-medium transition-colors ${form.punti_urto[k] ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-slate-700 bg-slate-900/30 p-4" data-testid="scheda-segnalatore">
            <h3 className="font-head text-sm uppercase tracking-wide text-teal-300 flex items-center gap-2 mb-3"><UserRound className="h-4 w-4" /> Segnalatore</h3>
            <Label className={LBL}>Segnalatore</Label>
            <SegnalatoreField value={form.segnalatore} onChange={(v) => set("segnalatore", v)} testId="a-segnalatore" />
            {(form.segnalatore || "").trim() && (
              <p className="mt-2 text-[11px] text-slate-500">La gestione parcelle e la provvigione del segnalatore si trovano ora nella sezione <b className="text-slate-400">Gestione Quietanze</b> del cliente.</p>
            )}
          </div>

          {isCedente && (
            <div className="mt-6 rounded-lg border border-amber-500/30 bg-amber-500/[0.04] p-4" data-testid="scheda-cessionario">
              <h3 className="font-head text-sm uppercase tracking-wide text-amber-300 flex items-center gap-2 mb-4"><ArrowRightLeft className="h-4 w-4" /> Scheda Cessionario</h3>
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
                <div className="md:col-span-2">
                  <Label className={LBL}>Cessionario</Label>
                  <div className="mt-1.5"><CessionarioSelect value={form.cessionario.ragione_sociale} onPick={pickCess} testId="a-cessionario" /></div>
                </div>
                <FieldText label="Ragione Sociale" value={form.cessionario.ragione_sociale} onChange={(v) => setCess("ragione_sociale", v)} testId="a-cess-ragione_sociale" />
                <FieldText label="Titolare" value={form.cessionario.titolare} onChange={(v) => setCess("titolare", v)} testId="a-cess-titolare" />
                <FieldText label="Codice Fiscale" value={form.cessionario.codice_fiscale} onChange={(v) => setCess("codice_fiscale", v.toUpperCase())} testId="a-cess-codice_fiscale" mono />
                <FieldText label="P.IVA" value={form.cessionario.partita_iva} onChange={(v) => setCess("partita_iva", v)} testId="a-cess-partita_iva" mono />
                <FieldText label="Via" value={form.cessionario.via} onChange={(v) => setCess("via", v)} testId="a-cess-via" />
                <FieldText label="N. Civico" value={form.cessionario.n_civico} onChange={(v) => setCess("n_civico", v)} testId="a-cess-n_civico" />
                <FieldText label="Comune" value={form.cessionario.comune} onChange={(v) => setCess("comune", v)} testId="a-cess-comune" />
                <FieldText label="Provincia" value={form.cessionario.provincia} onChange={(v) => setCess("provincia", v)} testId="a-cess-provincia" />
                <FieldText label="CAP" value={form.cessionario.cap} onChange={(v) => setCess("cap", v)} testId="a-cess-cap" />
                <FieldText label="IBAN" value={form.cessionario.iban} onChange={(v) => setCess("iban", v)} testId="a-cess-iban" mono />
              </div>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={saving} data-testid="assistito-submit" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {saving ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
